/**
 * Tiny helpers shared by report pages: date-range picker + section card.
 */
import React from 'react';
import { Calendar, ChevronLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { inputCls } from '../../../components/admin/billing/shared';

export const DateRange = ({ from, to, onFrom, onTo, onApply }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-end gap-2">
    <div className="flex-1 min-w-[140px]">
      <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1"><Calendar size={10} /> From</label>
      <input type="date" value={from} onChange={(e) => onFrom(e.target.value)} className={inputCls} data-testid="rep-date-from" />
    </div>
    <div className="flex-1 min-w-[140px]">
      <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">To</label>
      <input type="date" value={to} onChange={(e) => onTo(e.target.value)} className={inputCls} data-testid="rep-date-to" />
    </div>
    {onApply && (
      <button onClick={onApply} className="text-sm bg-teal-500 hover:bg-teal-600 text-white font-semibold px-4 py-2 rounded-lg" data-testid="rep-apply">Apply</button>
    )}
  </div>
);

export const ReportHeader = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2 flex-wrap">
    <Link to="/admin/reports" className="p-1.5 rounded hover:bg-slate-200" title="Back to Reports Hub" data-testid="rep-back">
      <ChevronLeft size={16} />
    </Link>
    <Icon className="text-teal-600" size={22} />
    <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{title}</h1>
  </div>
);

export const StatCard = ({ label, value, hint, color = 'text-slate-900', mono = true, testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    <div className={`text-xl font-bold ${color} ${mono ? 'font-mono' : ''}`}>{value}</div>
    {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
  </div>
);

export const monthFirst = () => {
  const d = new Date(); d.setDate(1);
  return d.toISOString().slice(0, 10);
};
export const today = () => new Date().toISOString().slice(0, 10);

// 1 April of the running Indian financial year — a far more useful default
// for money reports than "since the 1st of this month", which silently hid
// everything billed earlier.
export const fyStart = () => {
  const d = new Date();
  const year = d.getMonth() < 3 ? d.getFullYear() - 1 : d.getFullYear();
  return `${year}-04-01`;
};

// Invoice stream selector — GST books are the default across all reports.
export const INVOICE_SOURCES = [
  ['gst', 'GST only'],
  ['legacy', 'Legacy only'],
  ['all', 'Combined'],
];

export const SourceToggle = ({ value, onChange, idPrefix = 'source' }) => (
  <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid={`${idPrefix}-toggle`}>
    {INVOICE_SOURCES.map(([key, label]) => (
      <button
        key={key}
        type="button"
        onClick={() => onChange(key)}
        className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
          value === key ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-100'
        }`}
        data-testid={`${idPrefix}-${key}`}
      >
        {label}
      </button>
    ))}
  </div>
);
